/**
 * The ONE reader for `feedback.my_capture_targets()` — the capture anchor
 * (#967).
 *
 * ## Why it lives in `@solum/ui/shell`
 *
 * Two surfaces let a person file a capture and they live in different products:
 * the standalone extension and `apps/console`'s `/share` Web Share Target. Both
 * must ask the same question ("what may I anchor this to?"), honour the same
 * preselect, and send the same two arguments. A mapping written twice is a
 * mapping that drifts — rule 18, and the reason `invocations.ts` exists as one
 * seam rather than one per surface.
 *
 * `shell/` is where the composed capture EXPERIENCE already lives:
 * `declareContext.ts` is capture-domain code in this same directory, whose own
 * header argues the point (*"a React drawer IS chrome, so it lives with the
 * rest of the chrome"*). `CaptureAnchorFields.tsx` sits beside this file and
 * reads it.
 *
 * The alternatives were weighed rather than skipped:
 *
 *   · `@solum/sdk` — the natural home for a door reader, and where this file
 *     was first written. Rejected because the React half has to live with it or
 *     be written twice, and `@solum/ui` must NOT depend on `@solum/sdk`: that
 *     is the data client pointing INTO the design system. `RefPicker` shows the
 *     established direction — the APP imports both and joins them.
 *   · a copy in each app — rejected: the two would agree on the day they were
 *     written and never be compared again.
 *   · a new package for two files — rejected as ceremony.
 *
 * This file has NO imports. It is pure TypeScript over the door's own row
 * shape, so nothing about being in a React package reaches it.
 *
 * ## What is NOT here, on purpose
 *
 * **No preselect arithmetic.** `is_default` is computed by the door, in SQL
 * (`feedback/2026080813`), precisely so a client cannot have an opinion about
 * it. This file READS the flag; it never re-derives it. If the rule changes —
 * and it already has once, to cover a staff member whose only assignment is a
 * project — it changes in one place and both clients follow without a release.
 *
 * **No authority decision.** The list is what the caller may anchor to, and the
 * server's `capture_anchor` trigger admits exactly the same set. This is a
 * convenience for the person filing, not a control; sending an id that is not
 * in it is refused server-side whatever this file does.
 */

export type CaptureTargetKind = 'organization' | 'project'

/** One row of `feedback.my_capture_targets()`, in the door's own column names. */
export interface CaptureTarget {
  kind: CaptureTargetKind
  id: string
  label: string
  /** For an organization, its own id. For a project, the organization it pins. */
  account_id: string
  account_label: string
  /** The door's preselect. Read it; never compute it (see the header). */
  is_default: boolean
}

/** What a client has chosen. Either may be null; both null is a refusal. */
export interface CaptureAnchorSelection {
  organizationId: string | null
  projectId: string | null
}

/** Exactly the two `feedback.ingest_capture` parameters an anchor supplies. */
export interface CaptureAnchorArgs {
  p_account_id: string | null
  p_project_id: string | null
}

export const CAPTURE_TARGETS_FN = 'my_capture_targets'
export const CAPTURE_TARGETS_SCHEMA = 'feedback'

const isTarget = (v: unknown): v is CaptureTarget => {
  if (typeof v !== 'object' || v === null) return false
  const r = v as Record<string, unknown>
  return (r.kind === 'organization' || r.kind === 'project')
    && typeof r.id === 'string' && typeof r.account_id === 'string'
}

/**
 * Narrow whatever PostgREST answered into targets.
 *
 * A set-returning definer answers `[]` for "you are assigned to nothing" and
 * ALSO answers `null` when the call was mis-routed — a wrong `Content-Profile`,
 * a `feedback` missing from `PGRST_DB_SCHEMAS`. The two must not read alike:
 * the first is a refusal the user can act on, the second is a broken build, and
 * `submitCapture` already refuses to turn that same 200-with-null into "saved".
 */
export function readCaptureTargets(raw: unknown): CaptureTarget[] {
  if (raw === null || raw === undefined) {
    throw new Error(
      'feedback.my_capture_targets returned nothing — the call reached the server and answered no rows at all. ' +
      'Check that `feedback` is in PGRST_DB_SCHEMAS on this deployment.',
    )
  }
  if (!Array.isArray(raw)) throw new Error('feedback.my_capture_targets answered something that is not a list of targets.')
  return raw.filter(isTarget)
}

export const organizationsOf = (t: CaptureTarget[]): CaptureTarget[] => t.filter((r) => r.kind === 'organization')
export const projectsOf = (t: CaptureTarget[]): CaptureTarget[] => t.filter((r) => r.kind === 'project')

/**
 * The opening selection: whatever the door marked `is_default`.
 *
 * A default project also fixes the organization it pins, so the two fields are
 * never shown disagreeing — the server would refuse that combination anyway
 * and being told so after typing a bug report is not a UX.
 */
export function initialSelection(targets: CaptureTarget[]): CaptureAnchorSelection {
  const project = projectsOf(targets).find((r) => r.is_default)
  if (project) return { organizationId: project.account_id, projectId: project.id }
  const org = organizationsOf(targets).find((r) => r.is_default)
  return { organizationId: org?.id ?? null, projectId: null }
}

/**
 * Choosing a project moves the organization with it; choosing a different
 * organization drops a project that no longer belongs to it.
 *
 * This mirrors the trigger's first arm — *a project pins its organization* —
 * so the client cannot compose a pair the server will refuse.
 */
export function selectProject(targets: CaptureTarget[], projectId: string | null): CaptureAnchorSelection {
  if (projectId === null) return { organizationId: null, projectId: null }
  const p = projectsOf(targets).find((r) => r.id === projectId)
  return { organizationId: p?.account_id ?? null, projectId: p ? p.id : null }
}

export function selectOrganization(
  targets: CaptureTarget[], current: CaptureAnchorSelection, organizationId: string | null,
): CaptureAnchorSelection {
  if (organizationId === null) return { organizationId: null, projectId: null }
  const keep = current.projectId
    && projectsOf(targets).some((r) => r.id === current.projectId && r.account_id === organizationId)
  return { organizationId, projectId: keep ? current.projectId : null }
}

/** The two arguments, or `null` when there is nothing legitimate to send. */
export function anchorArgs(sel: CaptureAnchorSelection): CaptureAnchorArgs | null {
  if (!sel.organizationId && !sel.projectId) return null
  return { p_account_id: sel.organizationId, p_project_id: sel.projectId }
}

/**
 * The two things a person can be told, once each.
 *
 * NOT "something went wrong". The server refuses an unanchored capture with
 * "you are assigned to none", and a client that captures into the void rather
 * than saying so is precisely the behaviour #967 measured.
 */
export const NOT_ASSIGNED =
  'You are not assigned to an organization or a project, so a capture has nowhere to go. ' +
  'Ask to be added to the organization this report is about, or to one of its projects.'
export const CHOOSE_ANCHOR = 'Choose what this capture is about before sending it.'

/** Whether a draft may be sent at all, in the words the person should read. */
export function anchorRefusal(targets: CaptureTarget[], sel: CaptureAnchorSelection): string | null {
  if (targets.length === 0) return NOT_ASSIGNED
  if (!sel.organizationId && !sel.projectId) return CHOOSE_ANCHOR
  return null
}
